import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Loader2, CheckCircle2, AlertTriangle, ShieldAlert, Clock,
  ArrowRight, ExternalLink, RefreshCw, PackagePlus, XCircle,
} from "lucide-react";

/**
 * ConnectReturnPage
 * ------------------------------------------------------------------
 * Landing target after Stripe Express KYC. Polls /api/connect/status
 * every 2s (up to 30s) so we bridge the window between the seller
 * finishing KYC and the `account.updated` webhook landing. The status
 * endpoint already auto-refreshes from Stripe when charges_enabled is
 * false, so each poll effectively "asks Stripe again."
 *
 * Terminal states end the poll immediately:
 *   active · verification_required · restricted
 * Non-terminal states keep polling until timeout, then show the
 * "still processing" fallback.
 */

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;
const REDIRECT_COUNTDOWN_S = 5;
const TERMINAL_STATES = new Set(["active", "verification_required", "restricted"]);

export default function ConnectReturnPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const businessId = params.get("business_id") || undefined;

  const [status, setStatus] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const [busy, setBusy] = useState(false);
  const [countdown, setCountdown] = useState(REDIRECT_COUNTDOWN_S);
  const [redirectCancelled, setRedirectCancelled] = useState(false);

  const startedAt = useRef(Date.now());
  const pollTimer = useRef(null);
  const tickTimer = useRef(null);
  const stopped = useRef(false);

  const stopPolling = useCallback(() => {
    stopped.current = true;
    if (pollTimer.current) clearTimeout(pollTimer.current);
    if (tickTimer.current) clearInterval(tickTimer.current);
  }, []);

  const fetchOnce = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/connect/status`, {
        params: businessId ? { business_id: businessId } : {},
        withCredentials: true,
      });
      return r.data || null;
    } catch {
      return null;
    }
  }, [businessId]);

  // Polling loop with elapsed timer.
  useEffect(() => {
    let cancelled = false;
    tickTimer.current = setInterval(() => {
      setElapsed(Math.min(POLL_TIMEOUT_MS, Date.now() - startedAt.current));
    }, 250);

    const poll = async () => {
      if (cancelled || stopped.current) return;
      const data = await fetchOnce();
      if (cancelled) return;
      if (data) setStatus(data);
      const state = data?.state;
      if (state && TERMINAL_STATES.has(state)) {
        stopPolling();
        return;
      }
      if (Date.now() - startedAt.current >= POLL_TIMEOUT_MS) {
        setTimedOut(true);
        stopPolling();
        return;
      }
      pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [fetchOnce, stopPolling]);

  // Auto-redirect only on `active`, unless user cancelled.
  useEffect(() => {
    if (status?.state !== "active") return;
    if (redirectCancelled) return;
    setCountdown(REDIRECT_COUNTDOWN_S);
    const iv = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(iv);
          navigate("/dashboard", { replace: true });
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [status?.state, navigate, redirectCancelled]);

  const continueOnboarding = async () => {
    setBusy(true);
    try {
      const body = { origin_url: window.location.origin };
      if (businessId) body.business_id = businessId;
      const r = await axios.post(`${API}/connect/onboarding-link`, body, {
        withCredentials: true, timeout: 30000,
      });
      window.location.href = r.data.onboarding_url;
    } catch {
      toast.error("We couldn't reopen Stripe setup. Please try again.");
      setBusy(false);
    }
  };

  const openStripeDashboard = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/connect/dashboard-link`,
        businessId ? { business_id: businessId } : {},
        { withCredentials: true },
      );
      window.open(r.data.dashboard_url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("We couldn't open your Stripe dashboard. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const state = status?.state;
  const showRefreshing = !state || (!TERMINAL_STATES.has(state) && !timedOut);
  const pctElapsed = Math.min(100, Math.round((elapsed / POLL_TIMEOUT_MS) * 100));

  return (
    <div className="min-h-screen bg-tbc-cream py-16 px-4" data-testid="connect-return-page">
      <div className="max-w-xl mx-auto">
        {showRefreshing && (
          <RefreshingCard pct={pctElapsed} />
        )}
        {!showRefreshing && state === "active" && (
          <ActiveCard
            countdown={countdown}
            cancelled={redirectCancelled}
            onStay={() => setRedirectCancelled(true)}
          />
        )}
        {!showRefreshing && state === "verification_required" && (
          <VerificationRequiredCard
            requirements={status?.requirements}
            onContinue={continueOnboarding}
            busy={busy}
          />
        )}
        {!showRefreshing && state === "restricted" && (
          <RestrictedCard onOpenStripe={openStripeDashboard} busy={busy} />
        )}
        {!showRefreshing && timedOut && !TERMINAL_STATES.has(state) && (
          <StillProcessingCard onRefresh={() => window.location.reload()} />
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  State cards                                                          */
/* -------------------------------------------------------------------- */

function CardShell({ tone, children, testId }) {
  const toneMap = {
    neutral: "border-tbc-gold/40 bg-white",
    success: "border-emerald-300 bg-emerald-50",
    amber:   "border-amber-300 bg-amber-50",
    danger:  "border-red-300 bg-red-50",
    slate:   "border-slate-200 bg-white",
  };
  return (
    <div
      className={`rounded-2xl border-2 ${toneMap[tone] || toneMap.neutral} p-8 shadow-sm`}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

function RefreshingCard({ pct }) {
  return (
    <CardShell tone="neutral" testId="connect-return-refreshing">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-tbc-gold/15 border border-tbc-gold/40 flex items-center justify-center flex-shrink-0">
          <Loader2 className="w-7 h-7 text-tbc-gold animate-spin" />
        </div>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-widest text-tbc-gold font-semibold">
            Stripe · Payout Setup
          </p>
          <h1 className="text-2xl font-display text-tbc-navy mt-1">
            Refreshing status…
          </h1>
          <p className="mt-2 text-sm text-tbc-navy/70 font-body">
            We're waiting on Stripe to confirm your account. This usually takes
            a few seconds. Please don't close this tab.
          </p>
          <div className="mt-5 h-1.5 w-full rounded-full bg-tbc-gold/15 overflow-hidden">
            <div
              className="h-full bg-tbc-gold transition-[width] duration-300"
              style={{ width: `${pct}%` }}
              data-testid="connect-return-progress"
            />
          </div>
        </div>
      </div>
    </CardShell>
  );
}

function ActiveCard({ countdown, cancelled, onStay }) {
  return (
    <CardShell tone="success" testId="connect-return-active">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-emerald-100 border border-emerald-300 flex items-center justify-center flex-shrink-0">
          <CheckCircle2 className="w-7 h-7 text-emerald-600" />
        </div>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">
            Payouts Enabled
          </p>
          <h1 className="text-2xl font-display text-tbc-navy mt-1">
            You're ready to sell.
          </h1>
          <p className="mt-2 text-sm text-tbc-navy/80 font-body">
            Stripe just confirmed your account. Every sale on The Blk Community
            will now route straight to your bank.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/dashboard?tab=products">
              <Button
                className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy font-semibold rounded-full"
                data-testid="connect-return-add-product"
              >
                <PackagePlus className="w-4 h-4 mr-2" /> Add your first product
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button
                variant="outline"
                className="border-tbc-navy/20 text-tbc-navy rounded-full"
                data-testid="connect-return-go-dashboard"
              >
                Go to dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          {cancelled ? (
            <p
              className="mt-4 text-xs text-tbc-navy/60 font-body"
              data-testid="connect-return-countdown-cancelled"
            >
              Auto-redirect cancelled. Take your time.
            </p>
          ) : (
            <div className="mt-4 flex items-center gap-3">
              <p
                className="text-xs text-tbc-navy/60 font-body"
                data-testid="connect-return-countdown"
              >
                Redirecting to dashboard in {countdown}…
              </p>
              <button
                type="button"
                onClick={onStay}
                className="inline-flex items-center gap-1 text-xs font-semibold text-tbc-navy underline underline-offset-4 hover:text-tbc-gold transition-colors"
                data-testid="connect-return-stay"
              >
                <XCircle className="w-3.5 h-3.5" /> Stay here
              </button>
            </div>
          )}
        </div>
      </div>
    </CardShell>
  );
}

function VerificationRequiredCard({ requirements, onContinue, busy }) {
  const dueList = useMemo(() => {
    const src = requirements || {};
    const due = [...(src.currently_due || []), ...(src.past_due || [])];
    // Deduplicate + humanise.
    return Array.from(new Set(due)).slice(0, 6).map(humaniseRequirement);
  }, [requirements]);

  return (
    <CardShell tone="amber" testId="connect-return-verification">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-amber-100 border border-amber-300 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-7 h-7 text-amber-600" />
        </div>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-widest text-amber-700 font-semibold">
            Almost there
          </p>
          <h1 className="text-2xl font-display text-tbc-navy mt-1">
            Stripe needs a little more info.
          </h1>
          <p className="mt-2 text-sm text-tbc-navy/80 font-body">
            Your account is created but Stripe needs to verify a few more
            details before payouts turn on.
          </p>
          {dueList.length > 0 && (
            <ul className="mt-4 space-y-1.5 text-sm text-tbc-navy/80 font-body list-disc list-inside" data-testid="connect-return-requirements">
              {dueList.map((r) => <li key={r}>{r}</li>)}
            </ul>
          )}
          <div className="mt-6 flex flex-wrap gap-3">
            <Button
              onClick={onContinue}
              disabled={busy}
              className="bg-tbc-navy hover:bg-tbc-navy/90 text-white font-semibold rounded-full"
              data-testid="connect-return-continue"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Continue verification <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Link to="/dashboard">
              <Button variant="outline" className="border-tbc-navy/20 text-tbc-navy rounded-full">
                Back to dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </CardShell>
  );
}

function RestrictedCard({ onOpenStripe, busy }) {
  return (
    <CardShell tone="danger" testId="connect-return-restricted">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-red-100 border border-red-300 flex items-center justify-center flex-shrink-0">
          <ShieldAlert className="w-7 h-7 text-red-600" />
        </div>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-widest text-red-700 font-semibold">
            Account restricted
          </p>
          <h1 className="text-2xl font-display text-tbc-navy mt-1">
            Stripe has paused your payouts.
          </h1>
          <p className="mt-2 text-sm text-tbc-navy/80 font-body">
            This usually means Stripe needs a document or clarification.
            Open your Stripe dashboard to see the exact request and resolve it.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button
              onClick={onOpenStripe}
              disabled={busy}
              className="bg-tbc-navy hover:bg-tbc-navy/90 text-white font-semibold rounded-full"
              data-testid="connect-return-open-stripe"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Open Stripe dashboard <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
            <Link to="/dashboard">
              <Button variant="outline" className="border-tbc-navy/20 text-tbc-navy rounded-full">
                Back to dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </CardShell>
  );
}

function StillProcessingCard({ onRefresh }) {
  return (
    <CardShell tone="slate" testId="connect-return-pending">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-slate-100 border border-slate-300 flex items-center justify-center flex-shrink-0">
          <Clock className="w-7 h-7 text-slate-500" />
        </div>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-semibold">
            Still processing
          </p>
          <h1 className="text-2xl font-display text-tbc-navy mt-1">
            Stripe is taking a little longer than usual.
          </h1>
          <p className="mt-2 text-sm text-tbc-navy/80 font-body">
            No action needed on your end. You can safely head back to your
            dashboard — we'll email you the moment payouts turn on.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button
              onClick={onRefresh}
              className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy font-semibold rounded-full"
              data-testid="connect-return-refresh"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh status
            </Button>
            <Link to="/dashboard">
              <Button variant="outline" className="border-tbc-navy/20 text-tbc-navy rounded-full">
                Back to dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </CardShell>
  );
}

/* -------------------------------------------------------------------- */
/*  Helpers                                                              */
/* -------------------------------------------------------------------- */

function humaniseRequirement(key) {
  // Stripe requirement keys look like "individual.verification.document"
  // or "external_account". Turn them into readable phrases.
  const map = {
    "external_account": "Add a payout bank account",
    "individual.verification.document": "Upload a verification document",
    "individual.verification.additional_document": "Upload an additional verification document",
    "individual.id_number": "Provide your Social Security or Tax ID",
    "individual.ssn_last_4": "Provide the last 4 digits of your SSN",
    "individual.dob.day": "Confirm your date of birth",
    "business_profile.url": "Add your business website",
    "business_profile.mcc": "Confirm your business category",
    "tos_acceptance.date": "Accept Stripe's terms of service",
  };
  if (map[key]) return map[key];
  return key
    .replace(/^(individual|business_profile|company)\./, "")
    .replace(/[_.]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
